import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Browser } from "@capacitor/browser";
import { CreditCard, Home, Pencil, Plus, Trash2 } from "lucide-react";
import { Section, SectionActionButton } from "@shared/ui/containers/Section";
import { Card } from "@shared/ui/containers/Card";
import { BottomSheet } from "@shared/ui/containers/BottomSheet";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { Banner } from "@shared/ui/feedback/Banner";
import { Button } from "@shared/ui/primitives/Button";
import {
  CheckboxOption,
  SelectField,
  TextInputField,
} from "@shared/ui/forms/FormFields";
import { toast } from "@shared/ui/feedback/Toast";
import { env } from "@config/env";
import { platform } from "@shared/lib/platform";
import {
  createSavedAddress,
  deleteAccountPaymentMethod,
  deleteSavedAddress,
  fetchAccountPaymentMethods,
  fetchPaymentChannels,
  fetchSavedAddresses,
  requestTokenizationLink,
  updateSavedAddress,
  type AccountAddress,
  type AccountPaymentMethod,
} from "./billingApi";

export function ProfileBillingSections(): JSX.Element {
  return (
    <>
      <SavedAddressesSection />
      <PaymentMethodsSection />
    </>
  );
}

function SavedAddressesSection(): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AccountAddress | null>(null);
  const [adding, setAdding] = useState(false);
  const query = useQuery({
    queryKey: ["saved-addresses"],
    queryFn: fetchSavedAddresses,
  });
  const deleteMutation = useMutation({
    mutationFn: deleteSavedAddress,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["saved-addresses"] });
      toast.success("Address deleted");
    },
    onError: (e) =>
      toast.danger(
        "Couldn't delete address",
        e instanceof Error ? e.message : undefined,
      ),
  });

  return (
    <Section
      title="Saved addresses"
      action={
        <SectionActionButton onClick={() => setAdding(true)}>
          <Plus className="h-3 w-3" strokeWidth={1.75} />
          Add
        </SectionActionButton>
      }
    >
      {query.isLoading ? (
        <Skeleton className="h-20" />
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState
          icon={<Home className="h-7 w-7" strokeWidth={1.5} />}
          title="No saved addresses."
          description="Billing and delivery addresses will appear here."
          action={<Button onClick={() => setAdding(true)}>Add address</Button>}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {query.data!.map((address) => (
            <Card key={address.account_address_id} noGrain className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-headline text-ink">
                    {address.label || "Address"}
                  </p>
                  <p className="mt-1 whitespace-pre-line text-body-sm text-ink-secondary">
                    {formatAddress(address)}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {address.is_primary ? <Badge>Primary</Badge> : null}
                    {address.is_billing ? <Badge>Billing</Badge> : null}
                    {address.is_delivery ? <Badge>Delivery</Badge> : null}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Edit address"
                    onClick={() => setEditing(address)}
                  >
                    <Pencil className="h-4 w-4" strokeWidth={1.75} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Delete address"
                    onClick={() => {
                      if (window.confirm("Delete this address?"))
                        deleteMutation.mutate(address.account_address_id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {adding || editing ? (
        <AddressSheet
          address={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      ) : null}
    </Section>
  );
}

function PaymentMethodsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const [channelId, setChannelId] = useState("");
  const [error, setError] = useState("");
  const methodsQuery = useQuery({
    queryKey: ["account-payment-methods"],
    queryFn: fetchAccountPaymentMethods,
  });
  const channelsQuery = useQuery({
    queryKey: ["payment-channels"],
    queryFn: fetchPaymentChannels,
  });
  const selectedChannelId =
    channelId ||
    channelsQuery.data?.find((channel) => channel.is_default)
      ?.payment_channel_id ||
    channelsQuery.data?.[0]?.payment_channel_id ||
    "";

  const deleteMutation = useMutation({
    mutationFn: deleteAccountPaymentMethod,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["account-payment-methods"],
      });
      toast.success("Payment method deleted");
    },
    onError: (e) =>
      toast.danger(
        "Couldn't delete payment method",
        e instanceof Error ? e.message : undefined,
      ),
  });

  const tokenizationMutation = useMutation({
    mutationFn: () =>
      requestTokenizationLink({
        payment_channel_id: selectedChannelId,
        return_url: `${env.portalBaseUrl}/tokenization/netcash-callback`,
      }),
    onSuccess: async (response) => {
      const url = response.url ?? response.caller_url;
      if (!url) {
        setError(
          response.errorMessage ??
            "The payment provider did not return a tokenization link.",
        );
        return;
      }
      setError("");
      if (platform.isNative())
        await Browser.open({ url, presentationStyle: "popover" });
      else window.location.href = url;
    },
    onError: (e) =>
      setError(
        e instanceof Error ? e.message : "Could not start card tokenization.",
      ),
  });

  const startTokenization = () => {
    if (channelsQuery.isLoading) {
      setError(
        "Payment channels are still loading. Please try again in a moment.",
      );
      return;
    }
    if (!selectedChannelId) {
      setError("No payment channels are available for card tokenization.");
      return;
    }
    tokenizationMutation.mutate();
  };

  return (
    <Section
      title="Payment methods"
      action={
        <SectionActionButton onClick={startTokenization}>
          <Plus className="h-3 w-3" strokeWidth={1.75} />
          Add
        </SectionActionButton>
      }
    >
      {error ? (
        <Banner
          variant="danger"
          title="Payment setup failed"
          description={error}
          className="mb-3"
        />
      ) : null}
      {(channelsQuery.data ?? []).length > 1 ? (
        <SelectField
          label="Payment channel"
          value={selectedChannelId}
          onChange={(event) => setChannelId(event.target.value)}
          className="mb-3"
        >
          {channelsQuery.data!.map((channel) => (
            <option
              key={channel.payment_channel_id}
              value={channel.payment_channel_id}
            >
              {channel.name}
            </option>
          ))}
        </SelectField>
      ) : null}

      {methodsQuery.isLoading ? (
        <Skeleton className="h-20" />
      ) : methodsQuery.isError ? (
        <ErrorState onRetry={() => methodsQuery.refetch()} />
      ) : (methodsQuery.data ?? []).length === 0 ? (
        <EmptyState
          icon={<CreditCard className="h-7 w-7" strokeWidth={1.5} />}
          title="No saved payment methods."
          description="Add a card securely through your estate's payment provider."
          action={
            <Button
              loading={tokenizationMutation.isPending}
              onClick={startTokenization}
            >
              Add card
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {methodsQuery.data!.map((method) => (
            <Card
              key={method.account_payment_method_id}
              noGrain
              className="p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
                    <CreditCard className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-display text-headline text-ink">
                      {formatPaymentMethod(method)}
                    </p>
                    <p className="mt-1 text-caption text-ink-muted">
                      {[
                        method.gateway_name,
                        getCardExpiration(method),
                        method.is_default ? "Default" : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Saved payment method"}
                    </p>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Delete payment method"
                  onClick={() => {
                    if (
                      window.confirm(`Delete ${formatPaymentMethod(method)}?`)
                    ) {
                      deleteMutation.mutate(method.account_payment_method_id);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

function AddressSheet({
  address,
  onClose,
}: {
  address: AccountAddress | null;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(address?.label ?? "");
  const [line1, setLine1] = useState(address?.address_line1 ?? "");
  const [line2, setLine2] = useState(address?.address_line2 ?? "");
  const [city, setCity] = useState(address?.city ?? "");
  const [state, setState] = useState(address?.state ?? "");
  const [postalCode, setPostalCode] = useState(address?.postal_code ?? "");
  const [country, setCountry] = useState(address?.country ?? "");
  const [isPrimary, setIsPrimary] = useState(address?.is_primary ?? false);
  const [isBilling, setIsBilling] = useState(address?.is_billing ?? false);
  const [isDelivery, setIsDelivery] = useState(address?.is_delivery ?? false);
  const [error, setError] = useState("");

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!line1.trim() && !city.trim())
        throw new Error("Add at least a street line or city.");
      const payload = {
        label: label.trim(),
        address_line1: line1.trim(),
        address_line2: line2.trim(),
        city: city.trim(),
        state: state.trim(),
        postal_code: postalCode.trim(),
        country: country.trim(),
        is_primary: isPrimary,
        is_billing: isBilling,
        is_delivery: isDelivery,
        is_active: true,
      };
      return address
        ? updateSavedAddress(address.account_address_id, payload)
        : createSavedAddress(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["saved-addresses"] });
      toast.success(address ? "Address updated" : "Address added");
      onClose();
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : "Could not save address."),
  });

  return (
    <BottomSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={address ? "Edit address" : "Add address"}
    >
      <div className="flex flex-col gap-4 pb-4">
        {error ? (
          <Banner variant="danger" title="Check address" description={error} />
        ) : null}
        <TextInputField
          label="Label"
          value={label ?? ""}
          onChange={(event) => setLabel(event.target.value)}
          optional
        />
        <TextInputField
          label="Street line 1"
          value={line1 ?? ""}
          onChange={(event) => setLine1(event.target.value)}
        />
        <TextInputField
          label="Street line 2"
          value={line2 ?? ""}
          onChange={(event) => setLine2(event.target.value)}
          optional
        />
        <TextInputField
          label="City"
          value={city ?? ""}
          onChange={(event) => setCity(event.target.value)}
          optional
        />
        <TextInputField
          label="State / province"
          value={state ?? ""}
          onChange={(event) => setState(event.target.value)}
          optional
        />
        <TextInputField
          label="Postal code"
          value={postalCode ?? ""}
          onChange={(event) => setPostalCode(event.target.value)}
          optional
        />
        <TextInputField
          label="Country"
          value={country ?? ""}
          onChange={(event) => setCountry(event.target.value)}
          optional
        />
        <CheckboxOption
          label="Primary address"
          checked={isPrimary}
          onChange={setIsPrimary}
        />
        <CheckboxOption
          label="Billing address"
          checked={isBilling}
          onChange={setIsBilling}
        />
        <CheckboxOption
          label="Delivery address"
          checked={isDelivery}
          onChange={setIsDelivery}
        />
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save address
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

function Badge({ children }: { children: string }): JSX.Element {
  return (
    <span className="rounded-pill bg-primary-50 px-2 py-1 text-caption font-semibold text-primary-700">
      {children}
    </span>
  );
}

function formatAddress(address: AccountAddress): string {
  const parts = [
    address.address_line1,
    address.address_line2,
    [address.city, address.state, address.postal_code]
      .filter(Boolean)
      .join(", "),
    address.country,
  ].filter(Boolean);
  return parts.join("\n") || "No address provided";
}

function formatPaymentMethod(method: AccountPaymentMethod): string {
  if (method.payment_method_type === "card") {
    const card = method.card_type ? titleCase(method.card_type) : "Card";
    return `${card} .... ${method.card_last4 || "****"}`;
  }
  if (method.payment_method_type === "bank_account") {
    const type = method.bank_account_type
      ? titleCase(method.bank_account_type)
      : "Account";
    return `${type} .... ${method.bank_account_last4 || "****"}`;
  }
  return method.label || method.payment_method_type;
}

function getCardExpiration(method: AccountPaymentMethod): string {
  if (!method.card_exp_month || !method.card_exp_year) return "";
  return `${String(method.card_exp_month).padStart(2, "0")}/${String(method.card_exp_year).slice(-2)}`;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
